import React from "react";
import {
  TextField,
  Button,
  withStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import exchangeRates from "../../service/exchangeRates";
import DialogError from "../alertas/DialogError";
import { TERM_FIELDS, TERM_LABELS } from "./terms";

// Data de hoje em yyyy-MM-dd no fuso LOCAL. `toISOString()` converte para UTC e,
// à noite no Brasil/Paraguai, devolveria a data de amanhã.
function today() {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now - offset).toISOString().slice(0, 10);
}

// Publica/edita a cotação de um dia (à vista + cheque 30/60/90).
// Props: enterpriseId, rate? (edição), close(), save().
class RateDialog extends React.Component {
  constructor(props) {
    super(props);
    const rate = props.rate || {};
    this.state = {
      open: true,
      loading: false,
      quote_date: rate.quote_date ? String(rate.quote_date).slice(0, 10) : today(),
      base_currency: rate.base_currency || "USD",
      quote_currency: rate.quote_currency || "PYG",
      notes: rate.notes || "",
      showError: false,
      error: null,
      ...TERM_FIELDS.reduce(
        (acc, field) => ({
          ...acc,
          [field]: rate[field] == null ? "" : String(rate[field]),
        }),
        {}
      ),
    };
  }

  handleChange = (name) => (event) => {
    this.setState({ [name]: event.target.value });
  };

  fail = (title, message) =>
    this.setState({ loading: false, showError: true, error: { title, message } });

  save = async () => {
    if (!this.state.quote_date) {
      this.fail("Data obrigatória", "Informe a data da cotação.");
      return;
    }

    // Taxa vazia vira null (limpa o prazo); ao menos uma tem que estar preenchida.
    const rates = TERM_FIELDS.reduce((acc, field) => {
      const raw = String(this.state[field]).trim().replace(",", ".");
      acc[field] = raw === "" ? null : Number(raw);
      return acc;
    }, {});

    const invalid = TERM_FIELDS.find(
      (field) => rates[field] !== null && !(rates[field] > 0)
    );
    if (invalid) {
      this.fail(
        "Taxa inválida",
        `Informe um número maior que zero em "${TERM_LABELS[invalid]}".`
      );
      return;
    }

    if (TERM_FIELDS.every((field) => rates[field] === null)) {
      this.fail(
        "Nenhuma taxa informada",
        "Preencha ao menos uma das quatro taxas."
      );
      return;
    }

    this.setState({ loading: true });
    const payload = {
      quote_date: this.state.quote_date,
      base_currency: this.state.base_currency.toUpperCase(),
      quote_currency: this.state.quote_currency.toUpperCase(),
      notes: this.state.notes || null,
      ...rates,
    };

    try {
      if (this.props.rate) {
        await exchangeRates.update(this.props.rate.id, {
          ...payload,
          enterprise_id: this.props.enterpriseId,
        });
      } else {
        await exchangeRates.publish({
          ...payload,
          enterprise_id: this.props.enterpriseId,
        });
      }
      this.props.save();
    } catch (err) {
      this.fail(
        "Erro ao salvar cotação",
        (err && err.data && err.data.message) ||
          "Não foi possível salvar a cotação. Tente novamente."
      );
    }
  };

  render() {
    const { classes } = this.props;
    return (
      <Dialog
        open={this.state.open}
        onClose={() => this.props.close()}
        aria-labelledby="rate-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogError
          open={this.state.showError}
          onClose={() => this.setState({ showError: false })}
          title={this.state.error ? this.state.error.title : "Erro"}
          message={this.state.error ? this.state.error.message : ""}
          suggestion={"Verifique os campos e tente novamente."}
        />
        <DialogTitle id="rate-dialog-title">
          {this.props.rate ? "Editar cotação" : "Publicar cotação do dia"}
        </DialogTitle>
        <DialogContent>
          <TextField
            className={classes.text}
            autoFocus
            margin="dense"
            fullWidth
            type="date"
            label="Data da cotação *"
            InputLabelProps={{ shrink: true }}
            value={this.state.quote_date}
            onChange={this.handleChange("quote_date")}
          />

          <div className={classes.currencyRow}>
            <TextField
              className={classes.currency}
              margin="dense"
              label="Moeda base"
              inputProps={{ maxLength: 3 }}
              value={this.state.base_currency}
              onChange={this.handleChange("base_currency")}
            />
            <TextField
              className={classes.currency}
              margin="dense"
              label="Moeda cotada"
              inputProps={{ maxLength: 3 }}
              value={this.state.quote_currency}
              onChange={this.handleChange("quote_currency")}
            />
          </div>
          <Typography variant="caption" color="textSecondary">
            Quanto vale 1 {this.state.base_currency || "USD"} em{" "}
            {this.state.quote_currency || "PYG"}.
          </Typography>

          {TERM_FIELDS.map((field) => (
            <TextField
              key={field}
              className={classes.text}
              margin="dense"
              fullWidth
              type="number"
              label={TERM_LABELS[field]}
              value={this.state[field]}
              onChange={this.handleChange(field)}
            />
          ))}

          <TextField
            className={classes.text}
            margin="dense"
            fullWidth
            multiline
            label="Observação"
            value={this.state.notes}
            onChange={this.handleChange("notes")}
          />

          {!this.props.rate && (
            <Typography variant="caption" color="textSecondary">
              Publicar uma data que já existe atualiza a cotação daquele dia.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => this.props.close()} color="primary">
            Cancelar
          </Button>
          <Button
            onClick={this.save}
            color="primary"
            disabled={this.state.loading}
          >
            {this.state.loading && (
              <CircularProgress size={20} style={{ marginRight: 8 }} />
            )}
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

const styles = (theme) => ({
  text: {
    marginBottom: theme.spacing(1),
  },
  currencyRow: {
    display: "flex",
    gap: theme.spacing(2),
  },
  currency: {
    width: 120,
  },
});

export default withStyles(styles)(RateDialog);
